import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Building, Link2, Briefcase, Send, Loader2, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const FreelancerApplicationModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    portfolio: '',
    specialization: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    onClose();
    // Reset form after closing
    setTimeout(() => {
      setSubmitted(false);
      setError('');
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/freelancer-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          website: '',
          portfolio: '',
          specialization: '',
          message: ''
        });
      } else {
        const data = await response.json();
        if (Array.isArray(data.detail)) {
          const errorMessages = data.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
          setError(errorMessages || 'Validierungsfehler');
        } else {
          setError(data.detail || 'Fehler beim Senden der Bewerbung');
        }
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuche es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div 
        className="rounded-2xl border border-white/10 w-full max-w-3xl overflow-hidden flex flex-col" 
        style={{ backgroundColor: '#0a0a0a', maxHeight: 'calc(100vh - 40px)' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-white/10 shrink-0" style={{ backgroundColor: '#0a0a0a' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}>
              <Briefcase className="w-5 h-5 text-purple-400" />
            </div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Als Freelancer bewerben
            </h2>
          </div>
          <button 
            onClick={handleClose}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors text-xl"
          >
            ×
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          {submitted ? (
            <div className="text-center py-12">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}
              >
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Bewerbung gesendet!
              </h3>
              <p className="text-gray-400 mb-6">
                Vielen Dank! Wir melden uns innerhalb von 2-3 Werktagen bei dir.
              </p>
              <button 
                onClick={handleClose}
                className="px-8 py-3 rounded-xl font-medium transition-all bg-purple-500 hover:bg-purple-600"
              >
                Schließen
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400">
                  {error}
                </div>
              )}

              {/* Two column layout on desktop */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Persönliche Daten */}
                <div className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-400" />
                    Persönliche Daten
                  </h3>

                  <div className="space-y-4">
                    {/* Name */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Name *</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder="Max Mustermann"
                          className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">E-Mail *</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="max@beispiel.de"
                          className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Telefon</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+49 123 456789"
                          className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                        />
                      </div>
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Firma (optional)</label>
                      <div className="relative">
                        <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          placeholder="Meine Agentur GmbH"
                          className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Berufliche Erfahrung */}
                <div className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-purple-400" />
                    Berufliche Erfahrung
                  </h3>

                  <div className="space-y-4">
                    {/* Specialization */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Spezialisierung *</label>
                      <select
                        name="specialization"
                        value={formData.specialization}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-purple-500"
                        style={{ backgroundColor: '#1a1a1a' }}
                      >
                        <option value="">Bitte wählen...</option>
                        <option value="video_editing">Video-Editing / Schnitt</option>
                        <option value="motion_design">Motion Design / Animation</option>
                        <option value="color_grading">Color Grading</option>
                        <option value="sound_design">Sound Design / Audio</option>
                        <option value="vfx">Visual Effects (VFX)</option>
                        <option value="full_production">Full-Service Produktion</option>
                        <option value="other">Sonstiges</option>
                      </select>
                    </div>

                    {/* Website */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Website (optional)</label>
                      <div className="relative">
                        <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          placeholder="https://meineseite.de"
                          className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                        />
                      </div>
                    </div>

                    {/* Portfolio */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Portfolio / Showreel URL</label>
                      <div className="relative">
                        <Link2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="url"
                          name="portfolio"
                          value={formData.portfolio}
                          onChange={handleInputChange}
                          placeholder="https://vimeo.com/mein-showreel"
                          className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                        />
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Nachricht (optional)</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Erzähl uns etwas über dich..."
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded-xl font-bold text-lg transition-all bg-purple-500 hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Bewerbung absenden
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Mit dem Absenden stimmst du unserer{' '}
                <button type="button" onClick={() => navigate('/datenschutz')} className="underline hover:text-white">
                  Datenschutzerklärung
                </button>{' '}
                zu.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreelancerApplicationModal;
